// Final fix for the delete button logic
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { IProperty } from '@/types';
import Button from '@/components/shared/Button';
import Card from '@/components/shared/Card';
import UIMap from '@/components/shared/UIMap';
import UISimpleTable, { SimpleTableColumn } from '@/components/shared/UISimpleTable';
import UIPaginator from '@/components/shared/UIPaginator';
import UIProperty from '@/components/shared/UIProperty';
import UIConfirmationModal from '@/components/shared/UIConfirmationModal';
import useDeleteProperty from '@/hooks/useDeleteProperty';
import {
  ViewIcon,
  EditIcon,
  DeleteIcon,
} from './PropertyIcons';
import './PropertiesListSection.scss';

interface PropertiesListSectionProps {
  properties?: IProperty[];
  isLoading?: boolean;
  isFetching?: boolean;
  // onAddProperty?: () => void; // Удаляем пропс
  onPropertyClick?: (property: IProperty) => void;
  onEditProperty?: (property: IProperty) => void;
  onViewProperty?: (property: IProperty) => void;
  totalItems?: number;
  currentPage?: number;
  itemsPerPage?: number;
  totalPages?: number;
  hasNext?: boolean;
  hasPrev?: boolean;
  onPageChange?: (page: number) => void;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc' | null;
  onSort?: (column: string, direction: 'asc' | 'desc') => void;
}

const PropertiesListSection: React.FC<PropertiesListSectionProps> = ({
  properties = [],
  isLoading = false,
  isFetching = false,
  // onAddProperty, // Удаляем пропс
  onPropertyClick,
  onEditProperty,
  onViewProperty,
  totalItems = 0,
  currentPage = 1,
  itemsPerPage = 10,
  totalPages = 0,
  hasNext = false,
  hasPrev = false,
  onPageChange,
  sortColumn,
  sortDirection,
  onSort,
}) => {
  const navigate = useNavigate();
  const {
    isDeleting,
    deleteError,
    deleteProperty,
    propertyToDelete,
    isConfirmationModalOpen,
    closeConfirmationModal,
    confirmDeletion,
  } = useDeleteProperty();

  const displayProperties = useMemo(() => properties || [], [properties]);
  const displayTotalItems = totalItems || 0;

  // Удаляем handleAddProperty, так как кнопка перенесена
  // const handleAddProperty = () => {
  //   if (onAddProperty) {
  //     onAddProperty();
  //   } else {
  //     navigate('/dashboard/properties/add');
  //   }
  // };

  const handleRowClick = (property: IProperty) => {
    if (onPropertyClick) {
      onPropertyClick(property);
    } else {
      navigate(`/dashboard/properties/${property.id}`);
    }
  };

  const handleViewClick = useMemo(() => (e: React.MouseEvent, property: IProperty) => {
    e.stopPropagation();
    if (onViewProperty) {
      onViewProperty(property);
    } else {
      navigate(`/dashboard/properties/${property.id}`);
    }
  }, [navigate, onViewProperty]);

  const handleEditClick = useMemo(() => (e: React.MouseEvent, property: IProperty) => {
    e.stopPropagation();
    if (onEditProperty) {
      onEditProperty(property);
    } else {
      navigate(`/dashboard/properties/${property.id}/edit`);
    }
  }, [navigate, onEditProperty]);

  const handleDeleteClick = useMemo(() => (e: React.MouseEvent, property: IProperty) => {
    e.stopPropagation();
    deleteProperty(property);
  }, [deleteProperty]);

  const handleSort = (columnKey: string, direction: 'asc' | 'desc') => {
    if (onSort) {
      onSort(columnKey, direction);
    }
  };

  const handlePageChange = (page: number) => {
    if (onPageChange) {
      onPageChange(page);
    }
  };

  const formatAddress = (address: any) => {
    if (!address) return { line1: '', line2: '' };
    const line1 = address.street || '';
    const line2Parts = [];
    if (address.city) line2Parts.push(address.city);
    if (address.zip) line2Parts.push(address.zip);
    if (address.country) line2Parts.push(address.country);
    return { line1, line2: line2Parts.join(', ') };
  };

  const tableColumns: SimpleTableColumn[] = useMemo(() => [
    {
      key: 'property',
      label: 'PROPERTY NAME',
      width: '25%',
      sortable: true,
      sortDirection: sortColumn === 'property' ? sortDirection : null,
      showDivider: true,
      render: (value: any, row: IProperty) => (
        <UIProperty name={row.name} type={row.type} />
      ),
    },
    {
      key: 'address',
      label: 'ADDRESS',
      width: '30%',
      sortable: true,
      sortDirection: sortColumn === 'address' ? sortDirection : null,
      showDivider: true,
      render: (value: any, row: IProperty) => {
        const address = formatAddress(row.address);
        return (
          <div className="PropertiesListSection__address-info">
            <div className="PropertiesListSection__address-line1">{address.line1}</div>
            <div className="PropertiesListSection__address-line2">{address.line2}</div>
          </div>
        );
      },
    },
    {
      key: 'units',
      label: 'UNITS',
      width: '10%',
      sortable: true,
      sortDirection: sortColumn === 'units' ? sortDirection : null,
      showDivider: true,
      render: (value: any, row: IProperty) => (
        <div style={{ textAlign: 'center', fontWeight: 500 }}>
          {Array.isArray(row.units) ? row.units.length : row.units ?? 0}
        </div>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: '15%',
      sortable: false,
      render: (value: any, row: IProperty) => {
        const unitCount = Array.isArray(row.units) ? row.units.length : (typeof row.units === 'number' ? row.units : 0);
        return (
          <div className="PropertiesListSection__actions">
            <Button onClick={(e) => handleViewClick(e, row)} className="PropertiesListSection__action-button PropertiesListSection__action-button--view">
              <ViewIcon />
            </Button>
            <Button onClick={(e) => handleEditClick(e, row)} className="PropertiesListSection__action-button PropertiesListSection__action-button--edit">
              <EditIcon />
            </Button>
            <Button
              onClick={(e) => handleDeleteClick(e, row)}
              className="PropertiesListSection__action-button PropertiesListSection__action-button--delete"
              isDisabled={unitCount > 0}
            >
              <DeleteIcon />
            </Button>
          </div>
        );
      },
    },
  ], [sortColumn, sortDirection, handleViewClick, handleEditClick, handleDeleteClick]);

  const geoData = useMemo(() => {
    return displayProperties
      .filter(prop => prop.address?.latitude && prop.address?.longitude)
      .map(property => ({
        label: property.name,
        x: property.address?.longitude || 0,
        y: property.address?.latitude || 0,
        raw: { addresstype: 'building' as const, type: 'house' as const },
      }));
  }, [displayProperties]);

  if (isLoading) {
    return (
      <div className="PropertiesListSection PropertiesListSection--loading">
        <Card className="PropertiesListSection__card">
          <Card.Body><div className="PropertiesListSection__loading">Loading properties...</div></Card.Body>
        </Card>
      </div>
    );
  }

  if (!isLoading && !isFetching && displayProperties.length === 0) {
    return (
      <div className="PropertiesListSection">
        <Card className="PropertiesListSection__card">
          {/* Удаляем Card.Title полностью */}
          {/* <Card.Title className="PropertiesListSection__header">
            <span className="PropertiesListSection__title">List of properties</span>
          </Card.Title> */}
          <Card.Body className="PropertiesListSection__content">
            {/* Swapped order: Table first, then Map */}
            <div className="PropertiesListSection__table-container">
              <UISimpleTable data={[]} columns={tableColumns} onSort={handleSort} onRowClick={handleRowClick} getRowKey={row => row.id} className="PropertiesListSection__table" />
              <div style={{ textAlign: 'center', padding: '40px', color: '#637381' }}>No properties found. Try adjusting your search filters or add a new property.</div>
            </div>
            <div className="PropertiesListSection__map"><UIMap geoData={[]} className="PropertiesListSection__map-component" /></div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="PropertiesListSection">
        <Card className="PropertiesListSection__card">
          {/* Удаляем Card.Title полностью */}
          {/* <Card.Title className="PropertiesListSection__header">
            <span className="PropertiesListSection__title">List of properties</span>
          </Card.Title> */}
          <Card.Body className="PropertiesListSection__content">
            {/* Swapped order: Table first, then Map */}
            <div className="PropertiesListSection__table-container">
              <UISimpleTable data={displayProperties} columns={tableColumns} onSort={handleSort} onRowClick={handleRowClick} getRowKey={row => row.id} className="PropertiesListSection__table" />
              {displayTotalItems > itemsPerPage && (
                <UIPaginator currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} className="PropertiesListSection__pagination" />
              )}
            </div>
            <div className="PropertiesListSection__map"><UIMap geoData={geoData} className="PropertiesListSection__map-component" /></div>
          </Card.Body>
        </Card>
      </div>
      {propertyToDelete && (
        <UIConfirmationModal
          isOpen={isConfirmationModalOpen}
          onClose={closeConfirmationModal}
          onConfirm={confirmDeletion}
          title="Delete Property"
          message={`Are you sure you want to delete the property "${propertyToDelete.name}"? This action cannot be undone.`}
          confirmText="Delete"
          isConfirming={isDeleting}
          error={deleteError}
        />
      )}
    </>
  );
};

export default PropertiesListSection;
